import Relay from "@/relay/relayUtils"
import { RequireOnlyOne } from "@/types/util/RequireOnlyOne"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import DiscoDropdownItem, {
  DiscoDropdownItemProps,
} from "@disco-ui/dropdown/DiscoDropdownItem"
import DiscoIcon from "@disco-ui/icon/DiscoIcon"
import DiscoScrolledIntoView from "@disco-ui/scrolled-into-view/DiscoScrolledIntoView"
import { SelectOption } from "@disco-ui/select/DiscoSelect"
import DiscoSpinner from "@disco-ui/spinner/DiscoSpinner"
import DiscoTableSearchInput, {
  DiscoTableSearchInputSkeleton,
} from "@disco-ui/table/header/search/DiscoTableSearchInput"
import DiscoText from "@disco-ui/text/DiscoText"
import useDebounce from "@utils/hook/useDebounce"
import { TestIDProps } from "@utils/typeUtils"

type Props<T extends SelectOption> = TestIDProps &
  RequireOnlyOne<
    {
      options: (T & Pick<DiscoDropdownItemProps, "subtitle" | "icon" | "children">)[]
      children: React.ReactNode
    },
    "options" | "children"
  > & {
    onSearch?: (value: string) => void
    filters?: React.ReactNode
    selectedOptions?: T | T[] | null
    onOptionSelect?: (opt: T) => void
    pagination: Relay.RefetchablePagination
    emptyState?: React.ReactNode | string
  }

function DiscoPaginatedDropdownContent<T extends SelectOption>({
  testid = "DiscoPaginatedDropdownContent",
  onSearch,
  filters,
  options,
  children,
  selectedOptions,
  onOptionSelect,
  pagination,
  emptyState,
}: Props<T>) {
  const classes = useStyles()
  const debounceSearch = useDebounce(handleSearch, 500)
  const selectedOptionsSet = new Set(
    Array.isArray(selectedOptions)
      ? selectedOptions?.map((opt) => opt.value) ?? []
      : [selectedOptions?.value]
  )

  return (
    <div data-testid={testid}>
      <div className={classes.header}>
        {/* Search */}
        <DiscoTableSearchInput
          testid={`${testid}.search-input`}
          onChange={debounceSearch}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Filters */}
        {filters}
      </div>

      {/* Options */}
      <div className={classes.options}>
        {renderOptions()}

        {pagination.hasNext && (
          <DiscoScrolledIntoView
            isLoading={pagination.isLoading}
            onScrolledIntoView={() => pagination.loadMore({})}
            skeleton={<DiscoSpinner size={"sm"} />}
          />
        )}
      </div>
    </div>
  )

  function handleOptionSelect(option: T) {
    if (!onOptionSelect) return
    onOptionSelect(option)
  }

  async function handleSearch(value: string) {
    await pagination.refetch({ search: value, after: null })
    onSearch?.(value)
  }

  function renderOptions() {
    if (children) return children

    if (!options?.length) return renderEmptyState()

    return options.map((option, index) => {
      return (
        <DiscoDropdownItem
          key={option.value}
          title={option.title}
          subtitle={option.subtitle}
          icon={option.icon}
          testid={
            option.testid ? `${testid}.${option.testid}` : `${testid}.item-${index}`
          }
          onClick={() => handleOptionSelect(option)}
          rightButton={
            selectedOptionsSet.has(option.value) && (
              <DiscoIcon icon={"check"} marginLeft={1} />
            )
          }
        >
          {option.children}
        </DiscoDropdownItem>
      )
    })
  }

  function renderEmptyState() {
    if (!emptyState || typeof emptyState !== "string") {
      return (
        <DiscoText color={"text.secondary"} variant={"body-sm-500"} align={"center"}>
          {emptyState || "No results"}
        </DiscoText>
      )
    }

    return emptyState
  }
}

const useStyles = makeUseStyles((theme) => ({
  header: {
    display: "flex",
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    position: "sticky",
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: theme.zIndex.raise1,
  },
  options: {
    display: "flex",
    flexDirection: "column",
    height: "400px",
  },
}))

export function DiscoPaginatedDropdownContentSkeleton<
  T extends SelectOption<any | null, any>
>({ filters }: Pick<Props<T>, "filters">) {
  const classes = useStyles()
  return (
    <div>
      <div className={classes.header}>
        <DiscoTableSearchInputSkeleton />

        {filters}
      </div>

      <div className={classes.options}>
        <DiscoSpinner size={"sm"} />
      </div>
    </div>
  )
}

export default DiscoPaginatedDropdownContent
